'use client';

import type { CategoryData } from 'src/types/category-data';
import { isNil } from 'es-toolkit';

import { ActiveCategoriesFilterDropdown } from './active-categories-filter-dropdown';

type ActiveCategoriesFilterProps = {
  readonly categories: readonly CategoryData[];
  readonly selectedCategoryIds: readonly string[];
  readonly onRemoveCategory: (categoryId: string) => void;
  readonly onToggleCategory: (categoryId: string) => void;
  readonly onClearAll: () => void;
  readonly filterLabel: string;
};

type SelectedCategory = {
  readonly id: string;
  readonly name: string | null;
  readonly hexColorCode: string | null;
  readonly emoji: string | null;
};

export function ActiveCategoriesFilter({
  categories,
  selectedCategoryIds,
  onRemoveCategory,
  onToggleCategory,
  onClearAll,
  filterLabel,
}: ActiveCategoriesFilterProps) {
  const selectedCategories: SelectedCategory[] = selectedCategoryIds
    .map((categoryId) => {
      const category = categories.find((c) => c.id === categoryId);
      return category
        ? {
            id: category.id,
            name: category.name,
            hexColorCode: category.hexColorCode,
            emoji: category.emoji,
          }
        : null;
    })
    .filter((category): category is SelectedCategory => !isNil(category));

  if (selectedCategories.length === 0) return null;

  const MAX_VISIBLE_CATEGORIES = 3;
  const visibleCategories = selectedCategories.slice(0, MAX_VISIBLE_CATEGORIES);
  const remainingCount = selectedCategories.length - MAX_VISIBLE_CATEGORIES;
  const hasMoreCategories = remainingCount > 0;

  return (
    <ActiveCategoriesFilterDropdown
      allCategories={categories}
      selectedCategoryIds={selectedCategoryIds}
      onToggleCategory={onToggleCategory}
      onClearAll={onClearAll}
      visibleCategories={visibleCategories}
      remainingCount={remainingCount}
      hasMoreCategories={hasMoreCategories}
      onRemoveCategory={onRemoveCategory}
      filterLabel={filterLabel}
    />
  );
}
